/**
 * usePermissions Hook
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { canAccessRoute, getAccessibleRoutes, getDefaultRoute, hasAnyPermission, hasPermission, normalize } from '@/config/permissionConfig';

/**
 * Hook to access user permissions and permission-checking utilities
 */
export const usePermissions = () => {
  // Get user data from Redux store
  const user = useSelector((state) => state.auth?.authUser);
  
  // Extract permissions from user's roles
  const permissions = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles)) return [];
    
    // Flatten permissions from all roles
    const allPermissions = user.roles.reduce((acc, role) => {
      if (role.permissions && Array.isArray(role.permissions)) {
        acc.push(...role.permissions);
      }
      return acc;
    }, []);
    
    // Remove duplicates by permission id
    const uniquePermissions = allPermissions.filter(
      (perm, index, self) => index === self.findIndex(p => p.id === perm.id)
    );
    
    return uniquePermissions;
  }, [user]);

  // Permission IDs for quick lookup
  const permissionIds = useMemo(() => {
    return permissions.map(p => p.id);
  }, [permissions]);

  // Normalized permission names for matching
  const normalizedPermissionNames = useMemo(() => {
    return permissions.map(p => normalize(p.name));
  }, [permissions]);

  /**
   * Check if user has a specific permission by name
   */
  const checkPermission = (permissionName) => {
    return hasPermission(permissions, permissionName);
  };

  /**
   * Check if user has any of the given permissions
   */
  const checkAnyPermission = (permissionNames) => {
    return hasAnyPermission(permissions, permissionNames);
  };

  /**
   * Check if user has a specific permission by ID
   */
  const checkPermissionById = (permissionId) => {
    return permissionIds.includes(permissionId);
  };

  /**
   * Check if user has any of the given permission IDs
   */
  const checkAnyPermissionById = (ids) => {
    return ids.some(id => permissionIds.includes(id));
  };

  /**
   * Check if user can access a specific route
   */
  const checkRouteAccess = (routePath) => {
    return canAccessRoute(permissions, routePath);
  };

  /**
   * Get all routes user can access
   */
  const accessibleRoutes = useMemo(() => {
    return getAccessibleRoutes(permissions);
  }, [permissions]);

  /**
   * Get the default route for the user
   */
  const defaultRoute = useMemo(() => {
    return getDefaultRoute(permissions);
  }, [permissions]);

  return {
    // Raw data
    permissions,
    permissionIds,
    normalizedPermissionNames,
    
    // Check functions
    checkPermission,
    checkAnyPermission,
    checkPermissionById,
    checkAnyPermissionById,
    checkRouteAccess,
    
    // Computed values
    accessibleRoutes,
    defaultRoute,
    hasAnyPermissions: permissions.length > 0,
  };
};

export default usePermissions;